import React, { useState, useEffect } from "react";
import { fetchWithAuth } from "../../../auth/api";
import DeleteConfirmationModal from "../../../components/Home/DeleteConfirmationModal";
import {
  HiPhone,
  HiUser,
  HiChatAlt2,
  HiSearch,
  HiFilter,
  HiTrash,
  HiMail,
  HiPhotograph,
  HiX,
  HiChevronLeft,
  HiChevronRight,
  HiEye,
  HiExternalLink,
  HiClock,
} from "react-icons/hi";
import { toast } from "react-toastify";

const ContactList = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });
  const [viewModal, setViewModal] = useState({ isOpen: false, contact: null });
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0,
    senderName: "",
  });

  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth("get", "AllContacts");
        const sortedContacts = (response.data || []).sort(
          (a, b) => b.id - a.id
        );
        setContacts(sortedContacts);
      } catch (error) {
        toast.error("Failed to load contact submissions");
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, []);

  useEffect(() => {
    if (viewModal.isOpen || previewModal.isOpen || deleteModal.isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [viewModal.isOpen, previewModal.isOpen, deleteModal.isOpen]);

  const handleDelete = async (id) => {
    try {
      await fetchWithAuth("delete", `deleteContact/${id}`);
      setContacts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Contact inquiry deleted successfully");
    } catch (error) {
      toast.error("Failed to delete contact inquiry");
    } finally {
      setDeleteModal({ isOpen: false, id: null });
    }
  };

  const parseImages = (imagesData) => {
    if (!imagesData) return [];
    if (Array.isArray(imagesData)) return imagesData;
    try {
      const parsed = JSON.parse(imagesData);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return typeof imagesData === "string" ? [imagesData] : [];
    }
  };

  const openImagePreview = (imagesList, name, index = 0) => {
    setPreviewModal({
      isOpen: true,
      images: imagesList,
      currentIndex: index,
      senderName: name,
    });
  };

  const formatPhoneDisplay = (phone) => {
    if (!phone) return "No phone";
    const clean = phone.trim();
    if (clean.startsWith("+")) return clean;
    const digitsOnly = clean.replace(/[^0-9]/g, "");
    if (digitsOnly.length === 10 && digitsOnly.startsWith("9")) {
      return `+977 ${digitsOnly}`;
    }
    return clean;
  };

  const openWhatsApp = (phone, name) => {
    if (!phone) {
      toast.error("Phone number not available for this contact");
      return;
    }
    let cleanPhone = phone.replace(/[^0-9]/g, "");
    if (!cleanPhone) {
      toast.error("Invalid phone number format");
      return;
    }

    // Prepend Nepal country code 977 if missing (e.g. 98XXXXXXXX -> 97798XXXXXXXX)
    if (cleanPhone.length === 10 && cleanPhone.startsWith("9")) {
      cleanPhone = "977" + cleanPhone;
    } else if (!cleanPhone.startsWith("977") && cleanPhone.length < 12) {
      cleanPhone = "977" + cleanPhone;
    }

    const defaultMsg = encodeURIComponent(
      `Hi ${name || "there"}, thank you for contacting SD Enterprises. We received your inquiry.`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${defaultMsg}`, "_blank");
  };

  const openEmail = (email, name) => {
    if (!email) {
      toast.error("Email address not available for this contact");
      return;
    }
    const subject = encodeURIComponent("Response to your inquiry - SD Enterprises");
    const body = encodeURIComponent(
      `Hi ${name || "there"},\n\nThank you for contacting SD Enterprises regarding your inquiry.\n\nBest regards,\nSD Enterprises Team`
    );
    // Use direct Gmail Web compose link to avoid browser mailto network protocol errors
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}&su=${subject}&body=${body}`;
    window.open(gmailUrl, "_blank");
  };

  const filteredContacts = contacts.filter(
    (contact) =>
      contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: null })}
        onConfirm={() => handleDelete(deleteModal.id)}
      />

      {/* View Full Contact Details Modal */}
      {viewModal.isOpen && viewModal.contact && (
        <div className="fixed inset-0 z-[150] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 md:p-8 shadow-2xl space-y-6 animate-in zoom-in-95 duration-200 border border-slate-100 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-start border-b border-slate-100 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center font-bold border border-blue-100">
                  <HiUser size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900 tracking-tight">
                    {viewModal.contact.name}
                  </h3>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                    Inquiry #{viewModal.contact.id}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewModal({ isOpen: false, contact: null })}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-100 transition-colors"
              >
                <HiX size={20} />
              </button>
            </div>

            {/* Sender Meta Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
              <div className="space-y-1">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Email Address
                </span>
                <p className="text-xs font-bold text-slate-800 flex items-center gap-2">
                  <HiMail className="text-blue-500" />
                  {viewModal.contact.email}
                </p>
              </div>

              <div className="space-y-1">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Phone Number
                </span>
                <p className="text-xs font-bold text-slate-800 flex items-center gap-2">
                  <HiPhone className="text-emerald-500" />
                  {formatPhoneDisplay(viewModal.contact.phone)}
                </p>
              </div>

              {viewModal.contact.createdAt && (
                <div className="space-y-1 md:col-span-2">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Submitted Date
                  </span>
                  <p className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                    <HiClock className="text-slate-400" />
                    {new Date(viewModal.contact.createdAt).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {/* Message Description */}
            <div className="space-y-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Message / Technical Brief
              </span>
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 text-xs text-slate-700 leading-relaxed font-medium">
                "{viewModal.contact.description}"
              </div>
            </div>

            {/* Attached Photos */}
            {parseImages(viewModal.contact.images).length > 0 && (
              <div className="space-y-2">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Attached Reference Images ({parseImages(viewModal.contact.images).length})
                </span>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {parseImages(viewModal.contact.images).map((img, idx) => (
                    <div
                      key={idx}
                      onClick={() =>
                        openImagePreview(
                          parseImages(viewModal.contact.images),
                          viewModal.contact.name,
                          idx
                        )
                      }
                      className="aspect-square rounded-2xl overflow-hidden border border-slate-200 shadow-sm cursor-pointer hover:border-blue-500 hover:scale-105 transition-all bg-slate-100"
                    >
                      <img src={img} alt={`Attachment ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Modal Actions Footer */}
            <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center gap-3">
              <button
                onClick={() => openWhatsApp(viewModal.contact.phone, viewModal.contact.name)}
                className="w-full sm:flex-1 py-3 px-4 bg-emerald-600 text-white rounded-xl font-bold text-xs hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-100"
              >
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                </svg>
                Reply on WhatsApp
              </button>

              <button
                onClick={() => openEmail(viewModal.contact.email, viewModal.contact.name)}
                className="w-full sm:flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl font-bold text-xs hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-100"
              >
                <HiMail size={18} />
                Reply via Email
              </button>

              <button
                onClick={() => setViewModal({ isOpen: false, contact: null })}
                className="w-full sm:w-auto py-3 px-5 bg-slate-100 text-slate-700 rounded-xl font-bold text-xs hover:bg-slate-200 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Lightbox Modal */}
      {previewModal.isOpen && (
        <div className="fixed inset-0 z-[200] bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4">
          <button
            onClick={() => setPreviewModal({ isOpen: false, images: [], currentIndex: 0, senderName: "" })}
            className="absolute top-6 right-6 text-white bg-white/10 hover:bg-white/20 p-3 rounded-full transition-all"
          >
            <HiX size={24} />
          </button>

          <div className="max-w-4xl w-full flex flex-col items-center gap-4">
            <div className="text-center text-white">
              <h3 className="text-lg font-bold">Attachments from {previewModal.senderName}</h3>
              <p className="text-xs text-slate-400">
                Photo {previewModal.currentIndex + 1} of {previewModal.images.length}
              </p>
            </div>

            <div className="relative w-full max-h-[75vh] flex items-center justify-center">
              {previewModal.images.length > 1 && (
                <button
                  onClick={() =>
                    setPreviewModal((prev) => ({
                      ...prev,
                      currentIndex:
                        prev.currentIndex === 0
                          ? prev.images.length - 1
                          : prev.currentIndex - 1,
                    }))
                  }
                  className="absolute left-2 text-white bg-slate-900/60 hover:bg-blue-600 p-3 rounded-full transition-all"
                >
                  <HiChevronLeft size={24} />
                </button>
              )}

              <img
                src={previewModal.images[previewModal.currentIndex]}
                alt="Attachment Preview"
                className="max-h-[70vh] max-w-full rounded-2xl shadow-2xl object-contain border border-slate-700"
              />

              {previewModal.images.length > 1 && (
                <button
                  onClick={() =>
                    setPreviewModal((prev) => ({
                      ...prev,
                      currentIndex:
                        prev.currentIndex === prev.images.length - 1
                          ? 0
                          : prev.currentIndex + 1,
                    }))
                  }
                  className="absolute right-2 text-white bg-slate-900/60 hover:bg-blue-600 p-3 rounded-full transition-all"
                >
                  <HiChevronRight size={24} />
                </button>
              )}
            </div>

            {/* Thumbnail selector inside Modal */}
            {previewModal.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto max-w-full py-2">
                {previewModal.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setPreviewModal((prev) => ({ ...prev, currentIndex: idx }))}
                    className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition-all shrink-0 ${
                      idx === previewModal.currentIndex
                        ? "border-blue-500 scale-105 shadow-lg"
                        : "border-transparent opacity-50 hover:opacity-100"
                    }`}
                  >
                    <img src={img} alt={`Thumb ${idx}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <HiPhone className="text-blue-600" />
            Contact Inquiries
          </h2>
          <p className="text-slate-500 text-sm font-medium">
            Review messages, sender details, and attached photos from potential clients.
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-center bg-white p-4 rounded-2xl shadow-sm border border-slate-200/60 transition-all focus-within:shadow-md">
        <div className="relative flex-1 w-full text-slate-400 focus-within:text-blue-600">
          <HiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-xl transition-colors" />
          <input
            type="text"
            placeholder="Search inquiries by sender name, email, phone, or content..."
            className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm font-medium text-slate-700"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="flex items-center gap-2 px-5 py-3 text-slate-600 hover:bg-slate-50 rounded-xl border border-slate-200 transition-all font-bold text-sm">
          <HiFilter size={18} />
          <span>Refine</span>
        </button>
      </div>

      <div className="glass rounded-3xl overflow-hidden shadow-sm border border-slate-200/60 transition-all hover:shadow-md">
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-200/60">
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest w-20">
                  ID
                </th>
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest">
                  Sender Details
                </th>
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest">
                  Message Content
                </th>
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest">
                  Attached Photos
                </th>
                <th className="px-6 py-5 text-xs font-bold text-slate-400 uppercase tracking-widest text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-20 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="w-10 h-10 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
                      <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">
                        Retrieving messages...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : filteredContacts.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-20 text-center text-slate-400 font-bold uppercase tracking-widest text-xs"
                  >
                    No inquiries in your inbox
                  </td>
                </tr>
              ) : (
                filteredContacts.map((contact) => {
                  const imgList = parseImages(contact.images);
                  return (
                    <tr
                      key={contact.id}
                      className="hover:bg-slate-50/50 transition-colors group"
                    >
                      <td className="px-6 py-5">
                        <span className="text-xs font-bold text-slate-400">
                          #{contact.id}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="space-y-1">
                          <p className="text-sm font-bold text-slate-900 flex items-center gap-2">
                            <HiUser className="text-blue-500 shrink-0" />
                            {contact.name}
                          </p>
                          <p className="text-xs text-slate-500 font-medium flex items-center gap-1.5">
                            <HiMail className="text-slate-400 shrink-0" />
                            {contact.email}
                          </p>
                          <p className="text-xs text-slate-500 font-medium flex items-center gap-1.5">
                            <HiPhone className="text-slate-400 shrink-0" />
                            {formatPhoneDisplay(contact.phone)}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-start gap-2 max-w-md">
                          <HiChatAlt2 className="text-slate-300 mt-0.5 shrink-0" />
                          <p className="text-xs text-slate-600 font-medium leading-relaxed italic line-clamp-3">
                            "{contact.description}"
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        {imgList.length > 0 ? (
                          <div className="flex items-center gap-2">
                            {imgList.slice(0, 3).map((img, idx) => (
                              <div
                                key={idx}
                                onClick={() => openImagePreview(imgList, contact.name, idx)}
                                className="w-12 h-12 rounded-xl overflow-hidden border border-slate-200 shadow-sm cursor-pointer hover:scale-105 hover:border-blue-500 transition-all bg-slate-100 relative group/img"
                              >
                                <img
                                  src={img}
                                  alt="Thumbnail"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ))}
                            {imgList.length > 3 && (
                              <button
                                onClick={() => openImagePreview(imgList, contact.name, 3)}
                                className="w-12 h-12 rounded-xl bg-slate-900 text-white font-bold text-xs flex items-center justify-center shadow-sm hover:bg-blue-600 transition-colors"
                              >
                                +{imgList.length - 3}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">No attachments</span>
                        )}
                      </td>
                      <td className="px-6 py-5 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* View Details Button */}
                          <button
                            onClick={() => setViewModal({ isOpen: true, contact })}
                            className="text-[10px] font-black uppercase tracking-widest text-slate-700 hover:text-blue-600 hover:bg-blue-50 bg-slate-100 px-3 py-2 rounded-xl transition-all border border-slate-200/60 flex items-center gap-1.5"
                            title="View Full Details"
                          >
                            <HiEye size={14} className="text-slate-500" />
                            View
                          </button>

                          {/* WhatsApp Reply */}
                          <button
                            onClick={() => openWhatsApp(contact.phone, contact.name)}
                            className="text-[10px] font-black uppercase tracking-widest text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100 bg-emerald-50 px-3 py-2 rounded-xl transition-all border border-emerald-200 flex items-center gap-1.5"
                            title="Chat on WhatsApp"
                          >
                            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                              <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                            </svg>
                            WhatsApp
                          </button>

                          {/* Email Reply */}
                          <button
                            onClick={() => openEmail(contact.email, contact.name)}
                            className="text-[10px] font-black uppercase tracking-widest text-blue-600 hover:text-blue-700 hover:bg-blue-100 bg-blue-50 px-3 py-2 rounded-xl transition-all border border-blue-200 flex items-center gap-1.5"
                            title="Send Email"
                          >
                            <HiMail size={14} />
                            Email
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() =>
                              setDeleteModal({ isOpen: true, id: contact.id })
                            }
                            className="text-[10px] font-black uppercase tracking-widest text-red-600 hover:text-red-700 hover:bg-red-50 bg-red-50/50 px-3 py-2 rounded-xl transition-all border border-red-100 flex items-center gap-1"
                            title="Delete Inquiry"
                          >
                            <HiTrash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-slate-100">
          {loading ? (
            <div className="px-6 py-20 text-center">
              <div className="w-10 h-10 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
              <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">
                Retrieving messages...
              </span>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="px-6 py-20 text-center text-slate-400 font-bold uppercase tracking-widest text-xs">
              No inquiries in your inbox
            </div>
          ) : (
            filteredContacts.map((contact) => {
              const imgList = parseImages(contact.images);
              return (
                <div key={contact.id} className="p-6 space-y-4">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 border border-blue-100">
                        <HiUser size={20} />
                      </div>
                      <div>
                        <p className="text-sm font-black text-slate-900">
                          {contact.name}
                        </p>
                        <p className="text-xs text-slate-500 font-medium">
                          {contact.email} • {formatPhoneDisplay(contact.phone)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 italic">
                    <p className="text-xs text-slate-600 leading-relaxed">
                      "{contact.description}"
                    </p>
                  </div>

                  {imgList.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Attachments ({imgList.length})
                      </p>
                      <div className="flex gap-2 overflow-x-auto py-1">
                        {imgList.map((img, idx) => (
                          <div
                            key={idx}
                            onClick={() => openImagePreview(imgList, contact.name, idx)}
                            className="w-16 h-16 rounded-xl overflow-hidden border border-slate-200 shrink-0 bg-slate-100 cursor-pointer"
                          >
                            <img src={img} alt="Thumb" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <button
                      onClick={() => setViewModal({ isOpen: true, contact })}
                      className="py-3 text-[10px] font-black uppercase tracking-widest text-slate-700 bg-slate-100 rounded-xl border border-slate-200 flex items-center justify-center gap-2"
                    >
                      <HiEye size={14} />
                      View
                    </button>
                    <button
                      onClick={() => openWhatsApp(contact.phone, contact.name)}
                      className="py-3 text-[10px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 rounded-xl border border-emerald-200 flex items-center justify-center gap-2"
                    >
                      <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                      </svg>
                      WhatsApp
                    </button>
                    <button
                      onClick={() => openEmail(contact.email, contact.name)}
                      className="py-3 text-[10px] font-black uppercase tracking-widest text-blue-700 bg-blue-50 rounded-xl border border-blue-200 flex items-center justify-center gap-2"
                    >
                      <HiMail size={14} />
                      Email
                    </button>
                    <button
                      onClick={() =>
                        setDeleteModal({ isOpen: true, id: contact.id })
                      }
                      className="py-3 text-[10px] font-black uppercase tracking-widest text-red-600 bg-red-50 rounded-xl border border-red-100 flex items-center justify-center gap-2"
                    >
                      <HiTrash size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactList;
